import React, { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import { partnerOxbloodBtnSx, partnerOutlineBtnSx } from './PartnerListChrome';

export const OPS_REQUEST_TYPES = [
  'general',
  'itinerary_change',
  'booking_change',
  'transfer',
  'flight',
  'hotel',
  'activity',
  'document',
  'payment',
  'support',
  'recommendation',
  'other',
];

const PARTNER_SOURCES = ['partner', 'whatsapp', 'email'];
const PARTNER_PRIORITIES = ['critical', 'urgent', 'warning', 'info'];

/**
 * Create an in-trip ops request.
 * mode=partner → POST /partners/requests
 * mode=traveler → POST /trips/:tripId/ops-requests
 */
const CreateOpsRequestDialog = ({
  open,
  onClose,
  onCreated,
  mode = 'partner',
  tripId = null,
  tripLabel = '',
}) => {
  const { t } = useTranslation();
  const isPartner = mode === 'partner';
  const [title, setTitle] = useState('');
  const [type, setType] = useState(isPartner ? 'general' : 'itinerary_change');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState('partner');
  const [priority, setPriority] = useState('warning');
  const [selectedTripId, setSelectedTripId] = useState(tripId ? String(tripId) : '');
  const [trips, setTrips] = useState([]);
  const [loadingTrips, setLoadingTrips] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setError('');
    setTitle('');
    setDescription('');
    setType(isPartner ? 'general' : 'itinerary_change');
    setSource('partner');
    setPriority('warning');
    setSelectedTripId(tripId ? String(tripId) : '');
  }, [open, tripId, isPartner]);

  useEffect(() => {
    if (!open || !isPartner || tripId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        setLoadingTrips(true);
        const response = await api.get('/partners/trips', { usePartnerAuth: true });
        if (cancelled) return;
        const rows = (response.data?.trips || []).filter(
          (trip) => trip.managed_by_partner || trip.managedByPartner
        );
        setTrips(rows);
      } catch {
        if (!cancelled) setTrips([]);
      } finally {
        if (!cancelled) setLoadingTrips(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, isPartner, tripId]);

  const resolvedTripId = tripId || Number(selectedTripId);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!String(title).trim()) {
      setError(t('partnerOps.createTitleRequired'));
      return;
    }
    if (!resolvedTripId) {
      setError(t('partnerOps.createTripRequired'));
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      let created;
      if (isPartner) {
        const response = await api.post(
          '/partners/requests',
          {
            tripId: Number(resolvedTripId),
            title: title.trim(),
            type,
            description: description.trim() || null,
            source,
            priority,
          },
          { usePartnerAuth: true }
        );
        created = response.data?.request;
      } else {
        const response = await api.post(`/trips/${resolvedTripId}/ops-requests`, {
          title: title.trim(),
          type,
          description: description.trim() || null,
        });
        created = response.data?.request;
      }
      enqueueSnackbar(
        t(isPartner ? 'partnerOps.createSuccess' : 'trip.requestChangeSuccess'),
        { variant: 'success' }
      );
      onCreated?.(created);
      onClose?.();
    } catch (err) {
      setError(
        err.response?.data?.error
          || t(isPartner ? 'partnerOps.createFailed' : 'trip.requestChangeFailed')
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={isPartner ? 'partner-create-ops-request-dialog' : 'traveler-create-ops-request-dialog'}
    >
      <form onSubmit={handleSubmit}>
        <DialogTitle sx={{ fontFamily: DOCUMENT.display }}>
          {t(isPartner ? 'partnerOps.createTitle' : 'trip.requestChangeTitle')}
        </DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Typography sx={{ fontSize: 13.5, color: DOCUMENT.ink2 }}>
            {t(isPartner ? 'partnerOps.createHint' : 'trip.requestChangeHint')}
          </Typography>
          {error ? <Alert severity="error">{error}</Alert> : null}

          {isPartner && !tripId ? (
            <TextField
              select
              required
              fullWidth
              label={t('partnerOps.fieldTrip')}
              value={selectedTripId}
              onChange={(e) => setSelectedTripId(e.target.value)}
              disabled={loadingTrips}
              inputProps={{ 'data-testid': 'create-ops-request-trip' }}
            >
              {trips.map((trip) => (
                <MenuItem key={trip.id} value={String(trip.id)}>
                  {trip.title || trip.name || `#${trip.id}`}
                </MenuItem>
              ))}
            </TextField>
          ) : tripLabel ? (
            <Typography sx={{ fontSize: 13, color: DOCUMENT.ink2 }}>
              {t('partnerOps.fieldTrip')}: {tripLabel}
            </Typography>
          ) : null}

          <TextField
            required
            fullWidth
            label={t('partnerOps.createFieldTitle')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ 'data-testid': 'create-ops-request-title' }}
          />

          <TextField
            select
            fullWidth
            label={t('partnerOps.fieldType')}
            value={type}
            onChange={(e) => setType(e.target.value)}
            inputProps={{ 'data-testid': 'create-ops-request-type' }}
          >
            {OPS_REQUEST_TYPES.map((value) => (
              <MenuItem key={value} value={value}>
                {t(`partnerOps.requestType.${value}`, { defaultValue: value })}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            multiline
            minRows={3}
            label={t('partnerOps.fieldDescription')}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            inputProps={{ 'data-testid': 'create-ops-request-description' }}
          />

          {isPartner ? (
            <>
              <TextField
                select
                fullWidth
                label={t('partnerOps.fieldSource')}
                value={source}
                onChange={(e) => setSource(e.target.value)}
              >
                {PARTNER_SOURCES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`partnerOps.source.${value}`, { defaultValue: value })}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                fullWidth
                label={t('partnerOps.fieldPriority')}
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              >
                {PARTNER_PRIORITIES.map((value) => (
                  <MenuItem key={value} value={value}>
                    {t(`partnerOps.priority.${value}`, { defaultValue: value })}
                  </MenuItem>
                ))}
              </TextField>
            </>
          ) : null}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={onClose} sx={partnerOutlineBtnSx} disabled={submitting}>
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button
            type="submit"
            sx={partnerOxbloodBtnSx}
            disabled={submitting}
            data-testid="create-ops-request-submit"
          >
            {t(isPartner ? 'partnerOps.createSubmit' : 'trip.requestChangeSubmit')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default CreateOpsRequestDialog;
